<template>
  <AppDialog
    :show="show"
    max-width="960"
    persistent
    icon="mdi-pencil-outline"
    :title="order ? `Edit ${order.order_no}` : 'Edit Sale'"
    subtitle="Change quantities, prices or items - stock is adjusted by the difference only"
    @update:show="(v) => !v && close()"
    @close="close"
  >
    <div v-if="isLoading" class="text-center py-10">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <template v-else-if="order">
      <v-alert v-if="order.edit_blocked" type="warning" dense text>{{ order.edit_blocked }}</v-alert>

      <template v-else>
        <v-alert v-if="hasSpokenFor" type="info" dense text class="text-body-2">
          Some items were already returned or replaced - those lines keep their item and can't go below that quantity.
        </v-alert>

        <!-- Lines -->
        <v-simple-table dense class="edit-sale__table">
          <thead>
            <tr>
              <th>Item</th>
              <th class="text-center" style="width: 120px">Qty</th>
              <th class="text-right" style="width: 140px">Price (₹)</th>
              <th class="text-right" style="width: 120px">Total</th>
              <th style="width: 48px"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(line, i) in lines" :key="line._key">
              <td>
                <div class="font-weight-medium">{{ line.name }}</div>
                <div class="text-caption grey--text">
                  {{ line.variantLabel }}
                  <span v-if="line.minQty > 0" class="info--text"> · {{ line.minQty }} returned / replaced</span>
                  <span v-if="!line.id" class="success--text"> · new</span>
                </div>
              </td>
              <td>
                <v-text-field
                  v-model.number="line.quantity"
                  type="number"
                  :min="Math.max(1, line.minQty)"
                  dense
                  outlined
                  hide-details
                  class="edit-sale__num"
                ></v-text-field>
              </td>
              <td>
                <v-text-field v-model.number="line.price" type="number" min="0" dense outlined hide-details class="edit-sale__num"></v-text-field>
              </td>
              <td class="text-right mono-figure">₹{{ money(lineTotal(line)) }}</td>
              <td>
                <v-btn
                  icon
                  small
                  color="error"
                  :disabled="line.minQty > 0 || lines.length === 1"
                  :title="line.minQty > 0 ? 'Already returned / replaced - can\'t be removed' : lines.length === 1 ? 'A sale needs at least one item - delete the sale instead' : 'Remove this item'"
                  @click="lines.splice(i, 1)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-simple-table>

        <!-- Add an item -->
        <div class="pf-field-label mt-4 mb-2 font-weight-bold text-body-2">Add an item</div>
        <v-row dense align="center">
          <v-col cols="12" sm="5">
            <v-autocomplete
              v-model="picker.product_id"
              :items="products"
              item-text="name"
              item-value="id"
              label="Product"
              dense
              outlined
              hide-details
              clearable
              @change="picker.variant_id = null"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="3">
            <v-select
              v-model="picker.variant_id"
              :items="pickerVariants"
              :item-text="(v) => `${v.display_name || 'Default'} (${v.stock} in stock)`"
              item-value="id"
              label="Size / Colour"
              dense
              outlined
              hide-details
              :disabled="!picker.product_id"
            ></v-select>
          </v-col>
          <v-col cols="6" sm="2">
            <v-text-field v-model.number="picker.quantity" type="number" min="1" label="Qty" dense outlined hide-details></v-text-field>
          </v-col>
          <v-col cols="6" sm="2">
            <v-btn color="primary" depressed block :disabled="!picker.variant_id || !(picker.quantity > 0)" @click="addLine">
              <v-icon left small>mdi-plus</v-icon> Add
            </v-btn>
          </v-col>
        </v-row>

        <!-- Totals -->
        <v-divider class="my-4"></v-divider>
        <div class="d-flex justify-space-between text-body-2">
          <span class="grey--text">Items total (was ₹{{ money(order.total_amount) }})</span>
          <span class="mono-figure">₹{{ money(itemTotal) }}</span>
        </div>
        <div class="d-flex justify-space-between align-center mt-2">
          <span class="font-weight-bold">
            Final amount
            <span class="text-caption grey--text font-weight-regular">(was ₹{{ money(order.final_amount) }}; keeps any discount / delivery charge)</span>
          </span>
          <v-text-field
            v-model.number="finalAmount"
            type="number"
            min="0"
            dense
            outlined
            hide-details
            prefix="₹"
            style="max-width: 180px"
          ></v-text-field>
        </div>
        <p class="text-caption grey--text mt-3 mb-0">
          <template v-if="Number(order.payment_status) === 1">
            This sale is fully paid - it stays fully paid at the new amount; settle any difference with the customer at the counter.
          </template>
          <template v-else>₹{{ money(order.paid_amount || 0) }} collected so far stays as it is.</template>
          Items added need to be in stock; items reduced or removed go back into stock.
        </p>
      </template>
    </template>

    <template v-slot:actions>
      <v-btn text @click="close">Cancel</v-btn>
      <v-btn
        color="primary"
        depressed
        :loading="isSaving"
        :disabled="!order || !!order.edit_blocked || !isValid"
        @click="save"
      >
        Save changes
      </v-btn>
    </template>
  </AppDialog>
</template>

<script>
import axios from "axios";
import AppDialog from "@/components/AppDialog.vue";

// Edit a sale's items from the Orders list. Saves through
// POST orders/{id}/edit-items, which moves stock by the difference per item
// and keeps returns / replacements / Return Stock as they are.
export default {
  name: "EditSaleDialog",
  components: { AppDialog },
  props: {
    show: { type: Boolean, default: false },
    orderId: { type: [Number, String], default: null },
  },
  data() {
    return {
      order: null,
      lines: [],
      products: [],
      picker: { product_id: null, variant_id: null, quantity: 1 },
      finalAmount: 0,
      isLoading: false,
      isSaving: false,
    };
  },
  computed: {
    pickerVariants() {
      const product = this.products.find((p) => p.id === this.picker.product_id);
      return product ? product.variants || [] : [];
    },
    itemTotal() {
      return Math.round(this.lines.reduce((sum, l) => sum + this.lineTotal(l), 0) * 100) / 100;
    },
    hasSpokenFor() {
      return this.lines.some((l) => l.minQty > 0);
    },
    isValid() {
      return (
        this.lines.length > 0 &&
        this.lines.every((l) => Number.isInteger(Number(l.quantity)) && l.quantity >= Math.max(1, l.minQty) && l.price >= 0) &&
        this.finalAmount >= 0
      );
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(v) {
        if (v && this.orderId) this.load();
      },
    },
    // The final amount follows the item total, keeping the old discount / delivery difference
    itemTotal(total) {
      if (!this.order) return;
      const diff = Number(this.order.final_amount || 0) - Number(this.order.total_amount || 0);
      this.finalAmount = Math.max(0, Math.round((total + diff) * 100) / 100);
    },
  },
  methods: {
    money(v) {
      return (Number(v) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    lineTotal(line) {
      return (Number(line.quantity) || 0) * (Number(line.price) || 0);
    },
    variantLabel(v) {
      return [v && v.color, v && v.size].filter(Boolean).join(" / ");
    },
    async load() {
      this.isLoading = true;
      this.order = null;
      this.lines = [];
      this.picker = { product_id: null, variant_id: null, quantity: 1 };
      try {
        const [orderRes, mastersRes] = await Promise.all([
          axios.get(`/orders/${this.encryptIt(String(this.orderId))}`),
          this.products.length ? Promise.resolve(null) : axios.get("masters?context=purchase_order"),
        ]);
        if (mastersRes) this.products = mastersRes.data.products_with_all_variants || [];
        const order = orderRes.data.data;
        const spoken = order.spoken_for || {};
        this.lines = (order.order_details || []).map((d) => ({
          _key: `d${d.id}`,
          id: d.id,
          product_variant_id: d.product_variant_id,
          name: (d.product_variant && d.product_variant.product && d.product_variant.product.name) || "Item",
          variantLabel: this.variantLabel(d.product_variant),
          quantity: Number(d.quantity),
          price: Number(d.price),
          minQty: Number(spoken[d.id] || 0),
        }));
        this.order = order;
        this.finalAmount = Number(order.final_amount || order.total_amount || 0);
      } catch (e) {
        console.log(e);
        if (this.$toast) this.$toast.error("Could not open this sale.");
        this.close();
      } finally {
        this.isLoading = false;
      }
    },
    addLine() {
      const product = this.products.find((p) => p.id === this.picker.product_id);
      const variant = this.pickerVariants.find((v) => v.id === this.picker.variant_id);
      if (!product || !variant) return;
      const qty = Number(this.picker.quantity) || 1;
      // Same item already on the sale (and free to change): add to it
      const existing = this.lines.find((l) => l.product_variant_id === variant.id);
      if (existing) {
        existing.quantity = Number(existing.quantity) + qty;
      } else {
        this.lines.push({
          _key: `n${Date.now()}`,
          id: null,
          product_variant_id: variant.id,
          name: product.name,
          variantLabel: variant.display_name || "",
          quantity: qty,
          price: Number(variant.selling_price || variant.price || 0),
          minQty: 0,
        });
      }
      this.picker = { product_id: null, variant_id: null, quantity: 1 };
    },
    async save() {
      this.isSaving = true;
      try {
        await axios.post(`/orders/${this.encryptIt(String(this.orderId))}/edit-items`, {
          order_details: this.lines.map((l) => ({
            id: l.id || undefined,
            product_variant_id: l.product_variant_id,
            quantity: Number(l.quantity),
            price: Number(l.price),
          })),
          final_amount: Number(this.finalAmount),
        });
        if (this.$toast) this.$toast.success(`${this.order.order_no} updated - stock adjusted.`);
        this.$emit("saved");
        this.close();
      } catch (e) {
        console.log(e);
        const data = e.response && e.response.data;
        const message = (data && data.message) || "Could not save the changes.";
        if (this.$toast) this.$toast.error(message);
      } finally {
        this.isSaving = false;
      }
    },
    close() {
      this.$emit("update:show", false);
    },
  },
};
</script>

<style scoped>
.edit-sale__num {
  max-width: 130px;
  margin-left: auto;
}
.edit-sale__table td {
  vertical-align: middle;
  padding-top: 6px !important;
  padding-bottom: 6px !important;
}
</style>
